import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { companyChargeActions } from "../slices/companyCharge";
import { getErrorMessage } from "../../utils/errorUtils";
import { CompanyChargeRequestDto } from "../../shared/dtos/companyCharge/CompanyChargeRequestDto";
import { CompanyChargeResponseDto } from "../../shared/dtos/companyCharge/CompanyChargeResponseDto";
import { UpdateCompanyChargePayload } from "../../shared/interfaces/companyCharge/UpdateCompanyChargePayload";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const getCompanyCharges =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(companyChargeActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<CompanyChargeResponseDto[]>
      >("/company-charges", { headers: { requiresAuth: true } });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(companyChargeActions.setItems(data.data));
      }
    } catch (error) {
      dispatch(companyChargeActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getCompanyChargeById =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(companyChargeActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<CompanyChargeResponseDto>
      >(`/company-charges/${id}`, { headers: { requiresAuth: true } });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(companyChargeActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(companyChargeActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const createCompanyCharge =
  (values: CompanyChargeRequestDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(companyChargeActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<CompanyChargeResponseDto>
      >("/company-charges", values, { headers: { requiresAuth: true } });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Charge created successfully!");
        dispatch(companyChargeActions.createSuccess(data.data));
        dispatch(
          companyChargeActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      dispatch(companyChargeActions.setErrorCreate(getErrorMessage(error)));
    }
  };

export const updateCompanyCharge =
  (payload: UpdateCompanyChargePayload) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(companyChargeActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<CompanyChargeResponseDto>
      >(`/company-charges/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Charge updated successfully!");
        dispatch(companyChargeActions.updateSuccess(data.data));
        dispatch(
          companyChargeActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      dispatch(companyChargeActions.setErrorUpdate(getErrorMessage(error)));
    }
  };

export const deleteCompanyCharge =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(companyChargeActions.setLoadingDelete());
    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/company-charges/${id}`,
        { headers: { requiresAuth: true } },
      );
      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Charge deleted successfully!");
        dispatch(companyChargeActions.deleteSuccess(id));
        dispatch(
          companyChargeActions.setServerStatus({
            operation: "delete",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      dispatch(companyChargeActions.setErrorDelete(getErrorMessage(error)));
    }
  };

export const resetCompanyChargeServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(companyChargeActions.setServerStatus(null));
};
